// AYA PRO — Page catégorie : annuaire partenaires (fournisseurs, avocats…) ou teaser taxes.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProShell from "@/pro/ProShell";

const API = process.env.REACT_APP_BACKEND_URL || "";

function TaxesTeaser({ communes }) {
  return (
    <div className="pro-teaser" data-testid="pro-taxes-teaser">
      <span className="pro-soon-tag">Bientôt disponible</span>
      <h2>🏛️ Taxes & Mairie</h2>
      <p>
        Payez vos redevances et taxes communales directement dans AYA PRO : avis journaliers pilotés
        par votre mairie, paiement via le wallet AYA ou mobile money (Orange, MTN, Wave), et reçu
        officiel avec QR code de vérification. Partenariat en cours avec les 13 mairies du District d'Abidjan.
      </p>
      <div className="pro-communes-grid">
        {communes.map((c) => (
          <span key={c} className="pro-commune-pill">{c}</span>
        ))}
      </div>
    </div>
  );
}

function PartnerCard({ p }) {
  const metas = [
    p.moq ? `MOQ : ${p.moq}` : null,
    p.prix || null,
    p.delai ? `Délai : ${p.delai}` : null,
    p.specialite || null,
  ].filter(Boolean);
  return (
    <div className="pro-partner-card" data-testid={`pro-partner-${p.id}`}>
      <div className="pro-partner-head">
        <div>
          <div className="pro-partner-name">{p.name}</div>
          <div className="pro-partner-ville">📍 {p.ville}</div>
        </div>
        {p.badge && <span className="pro-partner-badge">✓ {p.badge}</span>}
      </div>
      <p className="pro-partner-desc">{p.desc}</p>
      <div className="pro-partner-meta">
        {metas.map((m) => <span key={m} className="pro-meta-pill">{m}</span>)}
        {(p.tags || []).map((t) => <span key={t} className="pro-meta-pill">#{t}</span>)}
      </div>
      <div className="pro-partner-actions">
        {p.whatsapp && (
          <a
            className="pro-btn pro-btn-gold"
            data-testid={`pro-partner-wa-${p.id}`}
            href={`https://wa.me/${p.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Bonjour, je vous contacte depuis AYA PRO au sujet de : ${p.name}`)}`}
            target="_blank"
            rel="noreferrer"
          >
            💬 WhatsApp
          </a>
        )}
        {p.whatsapp && (
          <a className="pro-btn pro-btn-outline" href={`tel:${p.whatsapp}`} data-testid={`pro-partner-call-${p.id}`}>
            📞 Appeler
          </a>
        )}
      </div>
    </div>
  );
}

export default function ProCategoryPage() {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registry, setRegistry] = useState(null);
  const [sector, setSector] = useState("Tous");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/v3/pro-space/directory/${catId}`).then((r) => r.json()),
      fetch(`${API}/api/v3/pro-space/registry`).then((r) => r.json()),
    ])
      .then(([d, reg]) => {
        if (d?.ok) setListings(d.listings || []);
        if (reg?.ok) setRegistry(reg);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    setSector("Tous");
  }, [catId]);

  const catMeta = useMemo(() => {
    for (const p of registry?.profiles || []) {
      const c = (p.categories || []).find((x) => x.id === catId);
      if (c) return c;
    }
    return null;
  }, [registry, catId]);

  const sectors = useMemo(() => {
    const s = [...new Set(listings.map((l) => l.sector).filter(Boolean))];
    return s.length > 1 ? ["Tous", ...s] : [];
  }, [listings]);

  const shown = sector === "Tous" ? listings : listings.filter((l) => l.sector === sector);

  return (
    <ProShell>
      <button className="pro-back" data-testid="pro-cat-back-btn" onClick={() => navigate("/pro")}>
        ← Retour à l'accueil PRO
      </button>
      <div className="pro-hero">
        <h1 data-testid="pro-cat-title">{catMeta ? `${catMeta.icon} ${catMeta.label}` : "Catégorie"}</h1>
        <p>{catMeta?.desc}</p>
      </div>

      {catId === "taxes-mairie" ? (
        <TaxesTeaser communes={registry?.communes || []} />
      ) : (
        <>
          {sectors.length > 0 && (
            <div className="pro-filter-row" data-testid="pro-sector-filters">
              {sectors.map((s) => (
                <button
                  key={s}
                  className={`pro-filter-chip ${s === sector ? "active" : ""}`}
                  data-testid={`pro-sector-chip-${s}`}
                  onClick={() => setSector(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="pro-listing" data-testid="pro-listing">
            {loading && <div className="pro-empty">Chargement…</div>}
            {!loading && shown.length === 0 && <div className="pro-empty">Aucun partenaire pour le moment.</div>}
            {shown.map((p) => <PartnerCard key={p.id} p={p} />)}
          </div>
        </>
      )}
    </ProShell>
  );
}
